import { useEffect, useRef, useState } from 'react';
import { KeyRound, LogIn, LogOut, MailCheck, MailWarning, RefreshCw, ShieldCheck, UserPlus, X } from 'lucide-react';
import { ApiError, api, type OAuthProviders, type SessionUser } from './api';

type AuthMode = 'login' | 'register' | 'forgot' | 'reset' | 'verify';

export type AuthUrlAction =
  | { type: 'verify-email'; token: string }
  | { type: 'reset-password'; token: string }
  | { type: 'oauth-success'; linked: boolean }
  | { type: 'oauth-error'; message: string };

export function consumeAuthUrlAction(): AuthUrlAction | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
  const verificationToken = fragment.get('verify-email');
  const resetToken = fragment.get('reset-password');
  const oauthResult = fragment.get('oauth');
  const oauthError = fragment.get('oauth-error');
  const action = verificationToken
    ? { type: 'verify-email' as const, token: verificationToken }
    : resetToken ? { type: 'reset-password' as const, token: resetToken }
      : oauthError ? { type: 'oauth-error' as const, message: oauthError }
        : oauthResult === 'success' || oauthResult === 'linked' ? { type: 'oauth-success' as const, linked: oauthResult === 'linked' } : null;
  if (!action) return null;

  // Consume and erase one-time secrets during module initialization, before React can issue requests.
  fragment.delete('verify-email');
  fragment.delete('reset-password');
  fragment.delete('oauth');
  fragment.delete('oauth-error');
  url.hash = fragment.toString();
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return action;
}

interface AuthDialogProps {
  currentUser: SessionUser | null;
  initialAction?: AuthUrlAction | null;
  onActionHandled?(): void;
  onClose(): void;
  onAuthenticated(user: SessionUser): void;
  onLogout(): void;
}

export function AuthDialog({ currentUser, initialAction = null, onActionHandled, onClose, onAuthenticated, onLogout }: AuthDialogProps): React.JSX.Element {
  const [mode, setMode] = useState<AuthMode>(() => initialAction?.type === 'verify-email' ? 'verify' : initialAction?.type === 'reset-password' ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() => initialAction?.type === 'oauth-error' ? initialAction.message : null);
  const [notice, setNotice] = useState<string | null>(() => initialAction?.type === 'oauth-success' ? initialAction.linked ? 'Внешний аккаунт успешно привязан.' : 'Вход через внешний аккаунт выполнен.' : null);
  const [oauthProviders, setOauthProviders] = useState<OAuthProviders>({ google: { enabled: false, linked: false }, yandex: { enabled: false, linked: false } });
  const [busy, setBusy] = useState(false);
  const verificationStartedRef = useRef(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [busy, onClose]);

  useEffect(() => {
    if (mode !== 'verify' || initialAction?.type !== 'verify-email' || verificationStartedRef.current) return;
    verificationStartedRef.current = true;
    setBusy(true);
    setError(null);
    api.verifyEmail(initialAction.token)
      .then(async ({ message }) => {
        setNotice(message || 'Электронная почта подтверждена. Теперь можно войти.');
        if (currentUser) {
          try {
            const { user } = await api.me();
            onAuthenticated(user);
          } catch {
            onAuthenticated({ ...currentUser, emailVerified: true });
          }
        }
        setMode('login');
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Ссылка подтверждения недействительна или устарела');
      })
      .finally(() => {
        setBusy(false);
        onActionHandled?.();
      });
  }, [currentUser, initialAction, mode, onActionHandled, onAuthenticated]);

  useEffect(() => {
    api.oauthProviders().then(({ providers }) => setOauthProviders(providers)).catch(() => undefined);
    if (initialAction?.type === 'oauth-success' || initialAction?.type === 'oauth-error') onActionHandled?.();
  }, [initialAction, onActionHandled]);

  const beginOauth = (provider: 'google' | 'yandex', link: boolean): void => {
    window.location.assign(`/api/auth/oauth/${provider}/start${link ? '?link=1' : ''}`);
  };

  const switchMode = (nextMode: AuthMode): void => {
    setMode(nextMode);
    setError(null);
    setNotice(null);
  };

  const submitCredentials = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'login') {
        const result = await api.login(email, password);
        onAuthenticated(result.user);
        onClose();
        return;
      }
      const result = await api.register(email, displayName, password);
      if (result.verificationRequired) {
        setVerificationEmail(result.user.email);
        setPassword('');
        setMode('login');
        setNotice('Пользователь создан. Мы отправили письмо со ссылкой для подтверждения адреса.');
        return;
      }
      onAuthenticated(result.user);
      onClose();
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'EMAIL_NOT_VERIFIED') setVerificationEmail(email.trim());
      setError(reason instanceof Error ? reason.message : 'Не удалось выполнить вход');
    } finally {
      setBusy(false);
    }
  };

  const requestPasswordReset = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.forgotPassword(email);
      setNotice(result.message || 'Если такой адрес зарегистрирован, мы отправили на него ссылку для восстановления пароля.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось отправить письмо для восстановления');
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (newPassword !== passwordConfirmation) {
      setError('Новые пароли не совпадают');
      return;
    }
    if (initialAction?.type !== 'reset-password') {
      setError('Ссылка восстановления не найдена. Запросите новое письмо.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.resetPassword(initialAction.token, newPassword);
      setNewPassword('');
      setPasswordConfirmation('');
      setMode('login');
      setNotice(result.message || 'Пароль изменён. Теперь можно войти.');
      if (currentUser) onLogout();
      onActionHandled?.();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось изменить пароль');
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (newPassword !== passwordConfirmation) {
      setError('Новые пароли не совпадают');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setPasswordConfirmation('');
      setMode('login');
      setNotice('Пароль изменён. В целях безопасности все сессии завершены — войдите с новым паролем.');
      onLogout();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось изменить пароль');
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async (targetEmail: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.resendVerification(targetEmail);
      setNotice(result.message || 'Если адрес ожидает подтверждения, новое письмо отправлено.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось отправить письмо повторно');
    } finally {
      setBusy(false);
    }
  };

  const logout = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.logout();
      onLogout();
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось завершить сессию');
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'verify' ? 'Подтверждение почты'
    : mode === 'reset' ? 'Новый пароль'
      : currentUser ? 'Профиль пользователя'
        : mode === 'register' ? 'Новый пользователь'
          : mode === 'forgot' ? 'Восстановление пароля'
            : 'Вход в систему';

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="dialog auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="dialog-heading"><div><span className="eyebrow">Freedom ISP Design</span><h2 id="auth-title">{title}</h2></div><button className="icon-button" type="button" aria-label="Закрыть" onClick={onClose} disabled={busy}><X size={18} /></button></div>

        {currentUser && mode !== 'verify' && mode !== 'reset' ? <>
          <div className="auth-profile-card">
            <div><strong>{currentUser.displayName}</strong><span>{currentUser.email}</span><small>Роль: {currentUser.role}</small></div>
            <span className={currentUser.emailVerified ? 'email-status verified' : 'email-status pending'}>{currentUser.emailVerified ? <><MailCheck size={14} /> Почта подтверждена</> : <><MailWarning size={14} /> Почта не подтверждена</>}</span>
          </div>
          {!currentUser.emailVerified ? <button className="secondary-action auth-resend" type="button" disabled={busy} onClick={() => void resendVerification(currentUser.email)}><RefreshCw size={15} /> Отправить письмо подтверждения повторно</button> : null}
          {(oauthProviders.google.enabled || oauthProviders.yandex.enabled) ? <div className="oauth-link-card">
            <h3>Внешние аккаунты</h3>
            <p>Привязанный аккаунт можно использовать для быстрого входа.</p>
            <div className="oauth-buttons">
              {oauthProviders.yandex.enabled ? <button type="button" className="oauth-button yandex" disabled={busy || oauthProviders.yandex.linked} onClick={() => beginOauth('yandex', true)}><span>Я</span>{oauthProviders.yandex.linked ? 'Яндекс ID привязан' : 'Привязать Яндекс ID'}</button> : null}
              {oauthProviders.google.enabled ? <button type="button" className="oauth-button google" disabled={busy || oauthProviders.google.linked} onClick={() => beginOauth('google', true)}><span>G</span>{oauthProviders.google.linked ? 'Google ID привязан' : 'Привязать Google ID'}</button> : null}
            </div>
          </div> : null}
          <form className="dialog-form password-change-form" onSubmit={(event) => void changePassword(event)}>
            <h3><KeyRound size={16} /> Сменить пароль</h3>
            <label><span>Текущий пароль</span><input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" required /></label>
            <label><span>Новый пароль</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" minLength={12} required /></label>
            <label><span>Повторите новый пароль</span><input type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} autoComplete="new-password" minLength={12} required /></label>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {notice ? <p className="form-notice" role="status">{notice}</p> : null}
            <button className="primary-action" type="submit" disabled={busy}>{busy ? 'Сохранение…' : 'Изменить пароль'}</button>
          </form>
          <button className="auth-logout" type="button" disabled={busy} onClick={() => void logout()}><LogOut size={16} /> Выйти</button>
          <p className="dialog-note">После смены пароля все активные сессии будут завершены.</p>
        </> : mode === 'verify' ? <div className="auth-action-state">
          <MailCheck size={30} />
          <h3>{busy ? 'Подтверждаем адрес…' : error ? 'Не удалось подтвердить адрес' : 'Адрес подтверждён'}</h3>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          {notice ? <p className="form-notice" role="status">{notice}</p> : null}
          {!busy ? <button className="primary-action" type="button" onClick={() => switchMode('login')}>Перейти ко входу</button> : null}
        </div> : mode === 'reset' ? <form className="dialog-form auth-single-form" onSubmit={(event) => void resetPassword(event)}>
          <p className="auth-form-intro">Придумайте новый пароль длиной не менее 12 символов.</p>
          <label><span>Новый пароль</span><input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" minLength={12} required /></label>
          <label><span>Повторите новый пароль</span><input type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} autoComplete="new-password" minLength={12} required /></label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          {notice ? <p className="form-notice" role="status">{notice}</p> : null}
          <button className="primary-action" type="submit" disabled={busy}>{busy ? 'Сохранение…' : 'Сохранить новый пароль'}</button>
          <button className="auth-link" type="button" onClick={() => switchMode('forgot')}>Запросить новую ссылку</button>
        </form> : mode === 'forgot' ? <form className="dialog-form auth-single-form" onSubmit={(event) => void requestPasswordReset(event)}>
          <p className="auth-form-intro">Укажите адрес учётной записи. Ответ не раскрывает, зарегистрирован ли пользователь.</p>
          <label><span>Электронная почта</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          {notice ? <p className="form-notice" role="status">{notice}</p> : null}
          <button className="primary-action" type="submit" disabled={busy}>{busy ? 'Отправка…' : 'Отправить ссылку'}</button>
          <button className="auth-link" type="button" onClick={() => switchMode('login')}><LogIn size={14} /> Вернуться ко входу</button>
        </form> : <>
          <div className="dialog-tabs"><button className={mode === 'login' ? 'active' : ''} type="button" onClick={() => switchMode('login')}><LogIn size={15} /> Войти</button><button className={mode === 'register' ? 'active' : ''} type="button" onClick={() => switchMode('register')}><UserPlus size={15} /> Регистрация</button></div>
          {(oauthProviders.google.enabled || oauthProviders.yandex.enabled) ? <>
            <div className="oauth-buttons">
              {oauthProviders.yandex.enabled ? <button type="button" className="oauth-button yandex" disabled={busy} onClick={() => beginOauth('yandex', false)}><span>Я</span>Продолжить с Яндекс ID</button> : null}
              {oauthProviders.google.enabled ? <button type="button" className="oauth-button google" disabled={busy} onClick={() => beginOauth('google', false)}><span>G</span>Продолжить с Google</button> : null}
            </div>
            <div className="auth-divider"><span>или по электронной почте</span></div>
          </> : null}
          <form className="dialog-form" onSubmit={(event) => void submitCredentials(event)}>
            {mode === 'register' ? <label><span>Имя</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" required /></label> : null}
            <label><span>Электронная почта</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label>
            <label><span>Пароль</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={12} required /></label>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {notice ? <p className="form-notice" role="status">{notice}</p> : null}
            {verificationEmail ? <div className="verification-reminder"><MailWarning size={18} /><span>Адрес <strong>{verificationEmail}</strong> ожидает подтверждения.</span><button type="button" disabled={busy} onClick={() => void resendVerification(verificationEmail)}>Отправить письмо повторно</button></div> : null}
            <button className="primary-action" type="submit" disabled={busy}>{busy ? 'Проверка…' : mode === 'login' ? 'Войти' : 'Создать пользователя'}</button>
            {mode === 'login' ? <button className="auth-link" type="button" onClick={() => switchMode('forgot')}><KeyRound size={14} /> Забыли пароль?</button> : null}
          </form>
          <p className="dialog-note"><ShieldCheck size={13} /> Пароли защищены Argon2id. Сессия хранится в HttpOnly cookie и автоматически истекает.</p>
        </>}
      </section>
    </div>
  );
}
